/**
 * @engine/core - Diagnostics
 * 
 * Performance monitoring and profiling.
 */

/**
 * Diagnostics data snapshot
 */
export interface DiagnosticsData {
  fps: number;
  frameTime: number;
  frameTimeMin: number;
  frameTimeMax: number;
  drawCalls: number;
  triangles: number;
  vertices: number;
  textures: number;
  memoryUsed: number;
  memoryTotal: number;
  gcCount: number;
  // Extended fields for renderer info
  gpuTime: number;
  renderer: string;
  backend: string;
  maxTextureSize: number;
  resolution: string;
  // GPU resource tracking
  vramUsed: number;
  vramAllocated: number;
  textureCount: number;
  bufferCount: number;
  pipelineChanges: number;
  bufferUploads: number;
  computeDispatches: number;
}

/**
 * Frame timing sample
 */
interface FrameSample {
  time: number;
  duration: number;
}

/**
 * Diagnostics - Performance monitoring
 */
export class Diagnostics {
  private static _instance: Diagnostics | null = null;
  
  // Frame timing
  private _frameTimes: number[] = [];
  private _lastFrameStart: number = 0;
  private _frameCount: number = 0;
  private _lastFpsUpdate: number = 0;
  private _currentFps: number = 0;
  
  // Stats
  private _drawCalls: number = 0;
  private _triangles: number = 0;
  private _textures: number = 0;
  
  // Config
  private _historySize: number = 60;
  private _fpsUpdateInterval: number = 500;
  private _enabled: boolean = true;
  
  private constructor() {}
  
  static get instance(): Diagnostics {
    if (!Diagnostics._instance) {
      Diagnostics._instance = new Diagnostics();
    }
    return Diagnostics._instance;
  }
  
  static reset(): void {
    Diagnostics._instance = null;
  }
  
  // ==========================================================================
  // FRAME TRACKING
  // ==========================================================================
  
  /**
   * Call at start of frame
   */
  beginFrame(): void {
    if (!this._enabled) return;
    this._lastFrameStart = performance.now();
  }
  
  /**
   * Call at end of frame
   */
  endFrame(): void {
    if (!this._enabled) return;
    
    const now = performance.now();
    const frameTime = now - this._lastFrameStart;
    
    // Track frame time history
    this._frameTimes.push(frameTime);
    if (this._frameTimes.length > this._historySize) {
      this._frameTimes.shift();
    }
    
    // Update FPS counter
    this._frameCount++;
    if (now - this._lastFpsUpdate >= this._fpsUpdateInterval) {
      this._currentFps = Math.round(
        (this._frameCount * 1000) / (now - this._lastFpsUpdate)
      );
      this._frameCount = 0;
      this._lastFpsUpdate = now;
    }
  }
  
  // ==========================================================================
  // RENDER STATS
  // ==========================================================================
  
  /**
   * Report draw call (call from renderer)
   */
  addDrawCall(triangles: number = 0): void {
    this._drawCalls++;
    this._triangles += triangles;
  }
  
  /**
   * Reset per-frame counters (call at frame start)
   */
  resetFrameCounters(): void {
    this._drawCalls = 0;
    this._triangles = 0;
  }
  
  /**
   * Set texture count
   */
  setTextureCount(count: number): void {
    this._textures = count;
  }
  
  // ==========================================================================
  // DATA ACCESS
  // ==========================================================================
  
  /**
   * Get current FPS
   */
  get fps(): number {
    return this._currentFps;
  }
  
  /**
   * Get average frame time (ms)
   */
  get frameTime(): number {
    if (this._frameTimes.length === 0) return 0;
    const sum = this._frameTimes.reduce((a, b) => a + b, 0);
    return sum / this._frameTimes.length;
  }
  
  /**
   * Get min frame time (ms)
   */
  get frameTimeMin(): number {
    if (this._frameTimes.length === 0) return 0;
    return Math.min(...this._frameTimes);
  }
  
  /**
   * Get max frame time (ms)
   */
  get frameTimeMax(): number {
    if (this._frameTimes.length === 0) return 0;
    return Math.max(...this._frameTimes);
  }
  
  /**
   * Get draw calls this frame
   */
  get drawCalls(): number {
    return this._drawCalls;
  }
  
  /**
   * Get triangles this frame
   */
  get triangles(): number {
    return this._triangles;
  }
  
  /**
   * Get texture count
   */
  get textures(): number {
    return this._textures;
  }
  
  /**
   * Get full diagnostics snapshot
   */
  getData(): DiagnosticsData {
    const mem = (performance as any).memory;
    
    return {
      fps: this._currentFps,
      frameTime: Math.round(this.frameTime * 100) / 100,
      frameTimeMin: Math.round(this.frameTimeMin * 100) / 100,
      frameTimeMax: Math.round(this.frameTimeMax * 100) / 100,
      drawCalls: this._drawCalls,
      triangles: this._triangles,
      vertices: 0,
      textures: this._textures,
      memoryUsed: mem ? Math.round(mem.usedJSHeapSize / 1024 / 1024) : 0,
      memoryTotal: mem ? Math.round(mem.totalJSHeapSize / 1024 / 1024) : 0,
      gcCount: 0, // Not easily trackable in JS
      // Extended renderer info
      gpuTime: 0,
      renderer: 'unknown',
      backend: 'unknown',
      maxTextureSize: 0,
      resolution: '0x0',
      // GPU resource tracking
      vramUsed: 0,
      vramAllocated: 0,
      textureCount: 0,
      bufferCount: 0,
      pipelineChanges: 0,
      bufferUploads: 0,
      computeDispatches: 0,
    };
  }
  
  /**
   * Get formatted string for display
   */
  getFormattedString(): string {
    const d = this.getData();
    return [
      `FPS: ${d.fps}`,
      `Frame: ${d.frameTime}ms (${d.frameTimeMin}-${d.frameTimeMax})`,
      `Draw Calls: ${d.drawCalls}`,
      `Triangles: ${d.triangles}`,
      `Textures: ${d.textures}`,
      d.memoryUsed > 0 ? `Memory: ${d.memoryUsed}/${d.memoryTotal}MB` : '',
    ].filter(Boolean).join('\n');
  }
  
  /**
   * Get frame time history for graphs
   */
  getFrameTimeHistory(): readonly number[] {
    return this._frameTimes;
  }
  
  // ==========================================================================
  // CONFIG
  // ==========================================================================
  
  /**
   * Enable/disable diagnostics
   */
  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    if (!enabled) {
      this._frameTimes = [];
    }
  }
  
  /**
   * Set history size
   */
  setHistorySize(size: number): void {
    this._historySize = Math.max(10, size);
    while (this._frameTimes.length > this._historySize) {
      this._frameTimes.shift();
    }
  }
  
  get enabled(): boolean {
    return this._enabled;
  }
}

export const diagnostics = Diagnostics.instance;
